/**
 * Conversion tracking: one delegated listener that turns clicks on downloads, call-to-actions,
 * contact links and outbound links into dataLayer events for Google Tag Manager.
 *
 * This file only describes what was clicked. Whether it reaches GA4 is decided in GTM: the
 * cookie banner (cookie-message.js) pushes `cookie_preferences` with `analytical: 0|1`, and every
 * GA4 event tag must be gated on `analytical` equals 1. Nothing here reads the consent cookie.
 *
 * Event and parameter names follow GA4's own (file_download, link_text, file_extension, …), so
 * the reports read like Enhanced Measurement's. That also means Enhanced Measurement's own
 * file-download and outbound-click tracking must be off, or those clicks count twice — see
 * DEPLOYMENT.md.
 *
 * What counts as what, first match wins:
 *   mailto: / tel:                          → contact_click
 *   `download` attribute or a file URL      → file_download
 *   .pq-button or data-track="cta"          → cta_click
 *   any other link to another host          → outbound_click
 * Everything else (menu, pagination, in-page anchors) is left to GA4's page views.
 *
 * `link_location` comes from the nearest `data-track-location` ancestor, which each component's
 * root element carries; links outside a component fall back to header / footer / content.
 */

const FILE_EXTENSION = /\.(pdf|docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|zip|mp4|mov|vtt|jpe?g|png|gif|webp|svg)$/i;

// Every key any event can carry. GTM's data model merges successive pushes, so a key left out of
// a push keeps its value from the previous one — a cta_click would inherit the file_name of the
// download clicked before it. Each push therefore sets all of them, undefined where unused.
const EMPTY = {
    link_text: undefined,
    link_url: undefined,
    link_domain: undefined,
    link_location: undefined,
    outbound: undefined,
    file_name: undefined,
    file_extension: undefined,
    contact_method: undefined,
};

export default class ClickTracking {
    constructor(el) {
        // Capture phase, so a handler that stops propagation further down can't hide a click.
        // auxclick catches middle-click "open in new tab", which downloads and outbound links get
        // often and a click listener never sees.
        el.addEventListener('click', ev => this.track(ev), { capture: true });
        el.addEventListener('auxclick', ev => ev.button === 1 && this.track(ev), { capture: true });
    }

    track(ev) {
        const link = ev.target.closest('a[href]');
        if (!link) return;

        const data = ClickTracking.describe(link);
        if (data) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ...EMPTY, ...data });
        }
    }

    static describe(link) {
        const href = link.getAttribute('href');
        const location = ClickTracking.location(link);

        // No address and no link text: both would be an e-mail address or phone number, which
        // is personal data, and GA4's terms forbid sending it.
        if (/^(mailto|tel):/i.test(href)) {
            return {
                event: 'contact_click',
                contact_method: /^tel:/i.test(href) ? 'phone' : 'email',
                link_location: location,
            };
        }

        let url;
        try {
            url = new URL(link.href);
        } catch {
            return null;
        }
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return null; // javascript:void(0)

        // www. and the bare domain are the same site; an editor pasting either must not make an
        // internal link count as outbound.
        const host = hostname => hostname.replace(/^www\./, '');
        const outbound = host(url.hostname) !== host(window.location.hostname);
        const common = {
            link_text: ClickTracking.text(link),
            link_url: url.href,
            link_domain: url.hostname,
            link_location: location,
            outbound,
        };

        if (link.hasAttribute('download') || FILE_EXTENSION.test(url.pathname)) {
            const fileName = decodeURIComponent(url.pathname.split('/').pop());
            const dot = fileName.lastIndexOf('.');
            return {
                event: 'file_download',
                ...common,
                file_name: fileName,
                file_extension: dot > 0 ? fileName.slice(dot + 1).toLowerCase() : undefined,
            };
        }

        if (link.dataset.track === 'cta' || link.classList.contains('pq-button')) {
            return { event: 'cta_click', ...common };
        }

        if (outbound) {
            return { event: 'outbound_click', ...common };
        }

        return null;
    }

    static location(link) {
        const component = link.closest('[data-track-location]');
        if (component) return component.dataset.trackLocation;
        if (link.closest('header')) return 'header';
        if (link.closest('footer')) return 'footer';
        return 'content';
    }

    // The label a visitor saw: aria-label for icon-only links, else the text, which on the
    // Downloads button includes its visually-hidden file title.
    static text(link) {
        const label = link.getAttribute('aria-label') || link.textContent;
        return label.replace(/\s+/g, ' ').trim().slice(0, 100) || undefined;
    }

    static init(selector) {
        [...document.querySelectorAll(selector)].forEach(el => new this(el));
    }
}
